import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import { getDashboard } from "./api";
import {
  ISSUE_META,
  STATUS_META,
  clockTime,
  fullName,
  hours,
  initials,
  longDate,
  shiftTime,
} from "./format";

const HISTORY_TONE = {
  present: "green",
  weekoff_present: "green",
  working: "blue",
  absent: "red",
  leave: "amber",
  weekoff: "grey",
};

const EmployeeModal = ({ row, reportDate, onClose, onOpenClient }) => {
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);
  const date = row.date || reportDate;

  useEffect(() => {
    const controller = new AbortController();
    getDashboard(`employees/${row.user_uuid}/`, { to_date: date }, controller.signal)
      .then(setDetail)
      .catch(() => setFailed(true));
    return () => controller.abort();
  }, [row.user_uuid, date]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const day = detail?.day || row;
  const profile = detail?.profile || row;
  const status = STATUS_META[day.attendance_status] || { label: day.attendance_status, tone: "grey" };
  const summary = detail?.history_summary;

  return (
    <div className="a2-modal-backdrop" onClick={onClose}>
      <div className="a2-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="a2-modal-head">
          <div className="a2-avatar">{initials(profile) || "?"}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{fullName(profile)}</div>
            <div className="a2-card-sub">
              {profile.employee_id || "—"} · {profile.designation || "—"}
            </div>
          </div>
          <span className={`a2-pill bg-${status.tone}`}>{status.label}</span>
          <button className="a2-btn a2-btn-ghost a2-icon-btn" aria-label="Close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="a2-modal-grid">
          <div><small>Date</small>{longDate(date)}</div>
          <div><small>Punch In</small>{clockTime(day.punch_in)}</div>
          <div><small>Punch Out</small>{clockTime(day.punch_out)}</div>
          <div><small>Working Hours</small>{hours(day.worked)}</div>
          <div>
            <small>Shift</small>
            {day.shift_name ? `${day.shift_name} · ${shiftTime(day.shift_from)} – ${shiftTime(day.shift_to)}` : "—"}
          </div>
          <div><small>Client</small>{profile.client_name || "—"}</div>
          <div><small>Location</small>{profile.location_name || "—"}</div>
          <div><small>Zone</small>{[profile.zone_name, profile.region_name, profile.state_name].filter(Boolean).join(", ") || "—"}</div>
          <div><small>Mobile</small>{profile.mobile || "—"}</div>
        </div>

        {day.issues?.length > 0 && (
          <div style={{ marginTop: 14, fontSize: 13 }}>
            Exceptions:{" "}
            {day.issues.map((i) => (
              <span key={i} className={`a2-pill bg-${ISSUE_META[i]?.tone || "grey"}`} style={{ marginRight: 4 }}>
                {ISSUE_META[i]?.label || i}
              </span>
            ))}
          </div>
        )}

        {detail?.punches?.length > 1 && (
          <div style={{ marginTop: 14, fontSize: 13 }}>
            <b>Punches</b>
            {detail.punches.map((p, i) => (
              <div key={i} className="a2-card-sub">
                {clockTime(p.check_in_time)} → {clockTime(p.check_out_time)}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <b style={{ fontSize: 13 }}>Last 14 days</b>
          {summary && (
            <span className="a2-card-sub" style={{ marginLeft: 8 }}>
              {summary.present + summary.weekoff_present} present · {summary.absent} absent · {summary.leave} leave
            </span>
          )}
          {failed && <div className="a2-card-sub">Could not load history.</div>}
          <div className="a2-history">
            {(detail?.history || []).map((h) => (
              <div key={h.date} className={`bg-${HISTORY_TONE[h.attendance_status] || "grey"}`} title={`${longDate(h.date)} · ${STATUS_META[h.attendance_status]?.label}`}>
                {dayjs(h.date).format("DD")}
              </div>
            ))}
          </div>
        </div>

        <div className="a2-modal-foot">
          {profile.client_name && (
            <button className="a2-btn" onClick={() => onOpenClient(row)}>Open client view</button>
          )}
          <button className="a2-btn a2-btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;
